import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { permissionNotice } from '@/components/feedback/permission-notice.store';
import { useAuthStore } from '@/app/store/auth.store';
import { useCan } from '@/features/roles/hooks/use-can';
import type { MenuCategoryKey, PermissionValue } from '@/features/roles/types/role.types';

// Per-page access control, uniform across every gated admin page.
//
// PRD 21 §4.5: LNB menus are shown to every Role, so a user without permission CAN click through to a
// page; the block happens on entry. §7.2 then fixes how that block looks — 권한 없음 → "접근 권한이
// 없습니다." as a **모달** (the same table uses 토스트 for network/save failures, so this is a deliberate
// distinction), repeated for 403 in §7.3. Error Standard §7.1 agrees: a permission failure is a
// Business Logic Error → Modal 필수, toast 금지.
//
// This used to raise a toast, which is the one thing those sources rule out. The notice now goes
// through a store rendered by PermissionNoticeHost, because a denied page returns `null` and so has no
// component left to host a dialog; the host also owns the redirect, firing it when the user
// acknowledges rather than yanking the route out from under an unread dialog (SHCT-171, SHCT-448).
//
// Safe against hydration flicker: AuthProvider blocks rendering behind a boot skeleton until `login()`
// has set `isAuthenticated` + `permissions` atomically, so a permitted user never renders a gated page
// with empty permissions.
//
// SHCT-629 (re-test) — QC reported the notice appearing on a page that then rendered its data:
// an admin whose Role had just been widened from NO_PERMISSION to READ/WRITE got the refusal
// modal AND the content behind it, and acknowledging it sent them to /home despite now having
// access. SHCT-629 itself created the window. The permission map is re-read on every protected
// navigation, but the read is asynchronous, and `usePermissionSync` lives in ProtectedRoute — a
// PARENT. React runs child effects before parent effects, so this hook decided on the map the
// PREVIOUS navigation left behind, which still said NO_PERMISSION. When the read landed the
// store updated, `allowed` flipped true and the page rendered, but nothing ever closed a notice
// that only `clear()` on [확인] could dismiss.
//
// So the ACCUSATION waits for a settled read of this path, and the notice tracks `allowed` in
// both directions instead of being fired once:
//   · pending  → say nothing yet. Refusing someone on last page's answer is the whole bug.
//   · denied   → show. A genuine refusal now costs one extra request, which is the price of not
//                accusing people wrongly.
//   · allowed  → clear. Covers a notice already on screen when the widening lands, and is what
//                makes the reported symptom impossible rather than merely unlikely.
//
// The RETURN VALUE deliberately does not wait. Gating rendering on the settle too would blank the
// content area for one request on every navigation an admin is perfectly entitled to make — a new
// flicker traded for the old one. The stale map is trusted to render, exactly as before this
// change, and the fresh read demotes if it must: that is the downgrade path (WRITE →
// NO_PERMISSION), which already worked and is untouched. The asymmetry is the point — being
// slower to accuse than to admit is the safe direction for a notice whose [확인] navigates away.
export function useRequirePermission(
  category: MenuCategoryKey,
  level: PermissionValue,
): boolean {
  const { pathname } = useLocation();
  const allowed = useCan(category, level);
  const syncedPath = useAuthStore((s) => s.permissionsSyncedPath);
  const settled = syncedPath === pathname;

  useEffect(() => {
    if (!settled) return;
    if (allowed) permissionNotice.clear();
    else permissionNotice.show();
  }, [allowed, settled]);

  return allowed;
}
